import React from 'react';
import { Navigation } from '@/components/navigation';
import { Footer } from '@/components/footer';
import { ServiceDetailPage } from '@/components/service-detail-page';
import { SEO } from '@/components/seo';

const mobileAppService = {
  name: "Mobile App Development",
  promise: "Native and cross-platform apps users love to use",
  primaryKPI: "4.8+ app store ratings average across client apps",
  signals: {
    bestFor: "Businesses ready to engage customers on mobile",
    timeline: "8-14 weeks from wireframes to app store",
    team: "Mobile architect + iOS developer + Android developer + UX designer",
    deliverables: "Native apps + backend + app store submission"
  },
  outputs: [
    "iOS and Android native apps OR React Native cross-platform",
    "App store submission and approval management",
    "Push notification system integration",
    "Analytics and crash reporting setup",
    "Backend API development and hosting",
    "App maintenance and update strategy"
  ],
  process: [
    {
      step: "UX Design & Prototyping",
      description: "Create intuitive mobile experiences through user-centered design",
      ownership: "We design, you approve UX flows",
      artifacts: ["Wireframes", "Interactive prototype", "Design system"]
    },
    {
      step: "Native Development",
      description: "Build for optimal performance using Swift, Kotlin, or React Native",
      ownership: "We develop, you test beta builds",
      artifacts: ["iOS build", "Android build", "API integration"]
    },
    {
      step: "Testing & Optimization",
      description: "Test on real devices and optimize performance",
      ownership: "We test and optimize, you provide feedback",
      artifacts: ["QA report", "Performance benchmarks", "Device compatibility matrix"]
    },
    {
      step: "App Store Launch",
      description: "Handle submission and approval for both app stores",
      ownership: "We manage submission, you approve listings",
      artifacts: ["App Store listing", "Google Play listing", "Marketing assets"]
    }
  ],
  proof: [
    {
      client: "Fitness startup",
      challenge: "Needed to acquire users quickly in competitive market",
      result: "Consumer app with viral referral features and social sharing",
      metric: "100K downloads in first 3 months"
    },
    {
      client: "Retail brand",
      challenge: "Mobile web experience wasn't converting",
      result: "Native app with personalized shopping and easy checkout",
      metric: "45% of online sales through mobile app"
    },
    {
      client: "Service marketplace",
      challenge: "High user churn after first booking",
      result: "Smart engagement features and push notifications",
      metric: "85% user retention at 30 days"
    }
  ],
  faqs: [
    {
      question: "Should we build native or cross-platform?",
      answer: "It depends on your needs. Native (Swift/Kotlin) offers best performance and platform-specific features - ideal for complex apps. React Native allows faster development and code sharing between iOS and Android - great for MVPs and content-driven apps. We'll recommend the best approach based on your requirements, timeline, and budget."
    },
    {
      question: "How long does app store approval take?",
      answer: "Apple typically reviews apps in 24-48 hours, while Google Play is usually same-day. However, first submissions may take longer if revisions are needed. We handle the entire process and know how to avoid common rejection reasons, ensuring smooth approval."
    },
    {
      question: "Do you handle app store optimization?",
      answer: "Yes. We create compelling app store listings including title optimization, keyword research, engaging descriptions, professional screenshots, and preview videos. ASO is crucial for organic discovery and downloads."
    },
    {
      question: "What about app updates and maintenance?",
      answer: "Apps require ongoing maintenance for OS updates, bug fixes, and feature additions. We offer maintenance packages or can train your team to handle updates. We recommend quarterly updates to maintain user engagement and fix issues."
    },
    {
      question: "Can you integrate with our website/backend?",
      answer: "Absolutely. Most apps need backend integration for user data, authentication, content, or e-commerce. We can integrate with your existing APIs or build a new backend using modern technologies like Supabase, Firebase, or custom Node.js/Python services."
    }
  ]
};

const MobileAppDevelopment = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Mobile App Development - iOS & Android Apps | AffinityAim"
        description="Native mobile app development for iOS and Android. 4.8+ store ratings, performance-focused. Launch in 8-14 weeks."
        canonical="https://www.affinityaim.com/services/mobile-app-development"
        keywords="mobile app development, iOS app development, Android app development, React Native"
        type="service"
      />
      <Navigation />
      <main className="pt-32">
        <ServiceDetailPage service={mobileAppService} />
      </main>
      <Footer />
    </div>
  );
};

export default MobileAppDevelopment;
